import * as React from 'react'
import { useDebouncedCallback } from 'use-debounce';
import { connect } from 'react-redux'
import styled, { css } from 'styled-components'
import { AppState } from '../../store'
import { AnyAction, Dispatch } from 'redux'
import { setHelpCenterState } from '../../store/help_center/actions'
import { HelpCenterState } from '../../store/help_center/types'
import { useTranslation } from 'react-i18next'
import Loader from '../../../help_center/components/Loader/Loader'
import { HelpCenterController } from '../../controllers'

const SearchContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`

const SearchInputContainer = styled.div`
  background-color: rgb(245, 245, 245);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  transition: 250ms;
  border: 1px solid rgba(15, 15, 15, 0.16);
  margin: 0px 2px 4px;
`

const SearchInput = styled.input`
  width: 100%;
  padding: 11.5px 10px;
  font-size: 14px;
  color: rgb(26, 26, 26);
  border: none;
  background-color: transparent;
  outline: none;

  &:focus {
    outline: none;
  }
`

const SearchInputAction = styled.div<{ clickable: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: black;

  ${(props) => props.clickable && css`
    cursor: pointer;
  `}

  svg {
    width: 16px;
    height: 16px;
  }

  ${Loader} {
    width: 16px;
    height: 16px;
  }
`

interface IStateToProps {
  helpCenterState: HelpCenterState
}

interface IDispatchToProps {
  setHelpCenterState: typeof setHelpCenterState
}

type IProps = IStateToProps & IDispatchToProps

const Search = (props: IProps) => {
  const { t } = useTranslation()
  const { helpCenterState, setHelpCenterState } = props
  const { search, isFetching } = helpCenterState
  const searchInput = React.useRef<HTMLInputElement>(null)

  const fetchSearch = async () => {
    try {
      setHelpCenterState({ ...helpCenterState, isFetching: true, })

      const articles = await HelpCenterController.search(search)

      setHelpCenterState({
        ...helpCenterState,
        articles: articles,
        isFetching: false,
      })
    } catch (ex) {
      console.error(ex)
    }
  }
  const debouncedSearch = useDebouncedCallback(fetchSearch, 500)

  const onSearchChange = (e) => {
    const newSearch = e.target.value

    setHelpCenterState({
      ...helpCenterState,
      search: newSearch,
      isFetching: newSearch.length > 0,
    })

    if (newSearch.length > 0) debouncedSearch()
  }

  const onActionClick = () => {
    if (search.length === 0) return
    if (isFetching) return

    setHelpCenterState({
      ...helpCenterState,
      articles: [],
      isFetching: false,
      search: '',
    })
  }

  return (
    <SearchContainer>
      <SearchInputContainer>
        <SearchInput
          ref={searchInput}
          placeholder={t('Search::Search for help')}
          value={search}
          onChange={onSearchChange}
        />
        <SearchInputAction onClick={onActionClick} clickable={search?.length > 0 && !isFetching}>
          {search.length === 0 && <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="7.5" cy="7.5" r="4.625" stroke="currentColor" strokeWidth="1.75"></circle><path d="M13.3813 14.6187C13.723 14.9604 14.277 14.9604 14.6187 14.6187C14.9604 14.277 14.9604 13.723 14.6187 13.3813L13.3813 14.6187ZM10.3813 11.6187L13.3813 14.6187L14.6187 13.3813L11.6187 10.3813L10.3813 11.6187Z" fill="currentColor"></path></svg>}
          {search?.length > 0 && <>
            {isFetching && <Loader />}
            {!isFetching && <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 16 16"><path stroke="currentColor" stroke-linecap="round" strokeWidth="1.7" d="M11.89 11.889 8 7.999m0 0L4.111 4.112M8.001 8l3.889-3.89M8 8l-3.889 3.889"></path></svg>}
          </>}
        </SearchInputAction>
      </SearchInputContainer>
    </SearchContainer>
  )
}

const mapStateToProps = (state: AppState): IStateToProps => {
  const {
    helpCenter: helpCenterState,
  } = state

  return {
    helpCenterState: helpCenterState,
  }
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): IDispatchToProps => {
  return {
    setHelpCenterState: (options) => dispatch(setHelpCenterState(options)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Search)